import { connect } from 'react-redux';
import { AppState } from '../../app-state';
import { ComponentSize } from '../../common/types/ComponentSize';
import { selectSourceDocumentIsEditable } from '../../documents/selectors/source-document.selectors';
import { setAnnotationTableColumnVisibility } from '../../ui-settings/actions/annotation-table-settings.actions';
import {
  createAnnotationRelation,
  deleteAnnotationChildRelations,
} from '../actions/annotation-relations.actions';
import {
  addToChainNesting,
  setDragTargetId,
  setSelectedAnnotations,
  splitAnnotation,
  toggleAnnotationSelection,
  triggerDeleteAnnotations,
  updateAnnotationTypes,
  updateAnnotationValues,
} from '../actions/annotations.actions';
import {
  AnnotationTableComponent,
  AnnotationTableFunctions,
  AnnotationTableParams,
} from '../components/annotation-table.component';
import { selectAnnotationTableRows } from '../selectors/annotation-relation.selectors';
import { selectActiveAnnotationSchema } from '../selectors/annotation-schema.selectors';
import { selectAnnotationsAreLoading } from '../selectors/annotation.selectors';
import { selectAnnotationTableColumnSettings } from '../selectors/ui.selectors';
import { selectCurrentPageOffsetRange } from '../selectors/annotation-pages.selectors';
import { selectUsername } from '../../accounts/selectors/users.selectors';
import { AnnotationSchema } from '../models/annotation-schema.model';
import { RelationType } from '../../lib/relations/relation-types';
import { Map } from 'immutable';
import {
  emphasizeAnnotationHighlight,
  removeAnnotationHighlightEmphasis,
  setFocusedAnnotationId,
} from '../actions/ui-annotations.actions';
import {
  selectAnnotationTableRowsWithDeleted,
  selectCompare,
  selectDiffFilterActive,
  selectDiffModeEnabled,
} from '../selectors/version-history.selectors';
import { selectPageByViewOrDefault } from '../../documents/selectors/page-controls.selectors';
import { PageView } from '../../documents/models/page-view.model';

export const AnnotationTable = connect(
  (
    state: AppState,
    ownProps: { size?: ComponentSize },
  ): AnnotationTableParams => {
    const diffMode = selectDiffModeEnabled(state);
    const compare = selectCompare(state);
    const rows = diffMode
      ? selectAnnotationTableRowsWithDeleted(state)
      : selectAnnotationTableRows(state);
    return {
      rows,
      columns: selectAnnotationTableColumnSettings(state),
      loading:
        selectAnnotationsAreLoading(state) ||
        (diffMode && compare.status === 'loading'),
      editable: selectSourceDocumentIsEditable(state),
      size: ownProps.size,
      lastManualAnnotationId: state.annotations.lastManualAnnotationId,
      activeUser: selectUsername(state),
      activeSchema:
        selectActiveAnnotationSchema(state) || AnnotationSchema.unknownSchema(),
      dragTargetState: state.annotations.dragTargetState,
      focusedAnnotationId: state._UI_annotations.focusedAnnotationId,
      latestPage: selectPageByViewOrDefault(PageView.Latest, state),
      pageOffsetRange: selectCurrentPageOffsetRange(state),
      filterCurrentPage: state.pageControls.filterCurrentPage,
      diffFilterActive: selectDiffFilterActive(state),
      selectedAnnotations: state.annotations.selectedAnnotations,
      chainNestingOrder: state.annotations.chainNestingOrder,
    };
  },
  (dispatch): AnnotationTableFunctions => ({
    onRemoveAnnotation: (id: string) =>
      dispatch(triggerDeleteAnnotations([id])),
    onCreateRelation: (
      leftOrBatch:
        | string
        | { left: string; right: string; relationType: RelationType }[],
      right?: string,
      relationType?: RelationType,
    ) => {
      if (Array.isArray(leftOrBatch)) {
        dispatch(createAnnotationRelation(leftOrBatch));
      } else {
        if (leftOrBatch === right) {
          dispatch(deleteAnnotationChildRelations([leftOrBatch]));
        }
        dispatch(
          createAnnotationRelation({
            left: leftOrBatch,
            right: right!,
            relationType: relationType!,
          }),
        );
      }
    },
    onHideColumn: (colId: string, hide: boolean) => {
      dispatch(setAnnotationTableColumnVisibility(colId, hide));
    },
    onDeleteRelations: (annotationIds: string[]) => {
      dispatch(deleteAnnotationChildRelations(annotationIds));
    },
    onSplitAnnotation: (annotationId: string) => {
      dispatch(splitAnnotation(annotationId));
    },
    setSelectedAnnotations: (ids: string[]) =>
      dispatch(setSelectedAnnotations(ids)),
    addToChainNesting: (id: string) => dispatch(addToChainNesting(id)),
    toggleAnnotationSelection: (id: string) =>
      dispatch(toggleAnnotationSelection(id)),
    setDragTargetId: (id: string) => dispatch(setDragTargetId(id)),
    updateAnnotationValues: (m: Map<string, string>) =>
      dispatch(updateAnnotationValues(m)),
    updateAnnotationTypes: (m: Map<string, string>) =>
      dispatch(updateAnnotationTypes(m)),
    clearFocusedAnnotationId: () => dispatch(setFocusedAnnotationId(null)),
    onRowMouseOver: (annotationId: string) =>
      dispatch(emphasizeAnnotationHighlight(annotationId)),
    onRowMouseOut: () => dispatch(removeAnnotationHighlightEmphasis('')),
  }),
)(AnnotationTableComponent);
